import React, { useState, useEffect, useRef } from 'react';
import './FAQ.css';

const faqData = [
  {
    question: 'Will recruiters know my application is automated?',
    answer:
      "No. Applications are submitted through official job portals exactly as you would manually. Cold emails are sent from your personal email address with natural, personalized language. There's no 'sent via ApplyAI' footprint.",
  },
  {
    question: 'Can I customize the cold emails before they\'re sent?',
    answer:
      'Absolutely. You can review, edit, or rewrite every email before it goes out. You can also set templates and tone preferences that ApplyAI follows for future emails. Nothing is ever sent without your approval.',
  },
  {
    question: 'Is my Gmail account safe?',
    answer:
      'Yes. We use OAuth 2.0 for authentication, so we never see or store your password. ApplyAI only requests send permissions and cannot read your inbox, personal emails, or attachments. You can revoke access anytime from your dashboard.',
  },
  {
    question: 'What happens when I get an interview?',
    answer:
      "You'll receive an email notification immediately. The application status in your dashboard updates to 'Interview Scheduled.' From there, it's all you. We handle the outreach, you handle the interview prep.",
  },
  {
    question: 'Can I pause or cancel anytime?',
    answer:
      "Yes. You can pause applications from your dashboard with one click and we'll stop sending until you resume. Cancellation is instant with no fees, and we delete your connection data immediately.",
  },
  {
    question: 'Does it work for full-time roles or just internships?',
    answer:
      'Currently ApplyAI is optimized for internship applications and entry-level roles. Full-time role support is on our roadmap. Join the waitlist in your dashboard to be notified.',
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReduced) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handleToggle = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section className="faq-section">
      <div
        className={`faq-inner faq-animate${visible ? ' faq-animate--visible' : ''}`}
        ref={containerRef}
      >
        <p className="faq-kicker">FAQ</p>
        <h2 className="faq-heading">Questions? We've got answers.</h2>
        <p className="faq-subtitle">
          Everything you need to know before getting started.
        </p>

        <div className="faq-accordion">
          {faqData.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className={`faq-item${isOpen ? ' faq-item--expanded' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => handleToggle(index)}
                  aria-expanded={isOpen}
                >
                  <span>{item.question}</span>
                  <span className="faq-toggle">+</span>
                </button>
                <div
                  className={`faq-answer-wrapper${isOpen ? ' faq-answer-wrapper--open' : ''}`}
                >
                  <p className="faq-answer">{item.answer}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="faq-contact">
          <p className="faq-contact-text">Still have questions?</p>
          <a href="#contact" className="faq-contact-link">
            Talk to a human{' '}
            <span className="faq-contact-arrow">&rarr;</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
